import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LiveCameraFeed from "./LiveCameraFeed";

export const ModuleDetail = ({ moduleId, modules = [] }) => {
  const navigate = useNavigate();
  const [currentData, setCurrentData] = useState({
    timestamp: "",
    time: "",
    channel1: -0.82,
    channel2: -0.769,
    channel3: -0.815,
    channel4: -0.773,
  });

  const [historicalData, setHistoricalData] = useState([]);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (modules.length > 0 && moduleId) {
      const index = modules.findIndex((m) => m.id === moduleId);
      setCurrentModuleIndex(index >= 0 ? index : 0);
    }
  }, [modules, moduleId]);

  useEffect(() => {
    const fetchModuleData = () => {
      const newData = {
        timestamp: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        channel1: -0.82 + Math.random() * 0.01,
        channel2: -0.769 + Math.random() * 0.01,
        channel3: -0.815 + Math.random() * 0.01,
        channel4: -0.773 + Math.random() * 0.01,
      };

      setCurrentData(newData);
      setHistoricalData((prev) => [...prev, newData].slice(-20));
    };

    fetchModuleData();
    const interval = setInterval(fetchModuleData, 1000);

    return () => clearInterval(interval);
  }, [currentModuleIndex]);

  const handlePreviousModule = () => {
    if (currentModuleIndex > 0) {
      navigate(`/monitoring/module/${modules[currentModuleIndex - 1].id}`);
    }
  };

  const handleNextModule = () => {
    if (currentModuleIndex < modules.length - 1) {
      navigate(`/monitoring/module/${modules[currentModuleIndex + 1].id}`);
    }
  };

  const exportData = () => {
    const headers = "timestamp,time,channel1,channel2,channel3,channel4\n";
    const csvContent = historicalData
      .map(
        (row) =>
          `${row.timestamp},${row.time},${row.channel1},${row.channel2},${row.channel3},${row.channel4}`
      )
      .join("\n");

    const blob = new Blob([headers + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `module-${moduleId}-data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const currentModule = modules[currentModuleIndex] || {
    name: `Module ${currentModuleIndex + 1}`,
    platform: "Unknown Platform",
    leg: "Unknown Leg",
    location: "Unknown Location",
  };

  const getStatusClass = (value) => {
    if (value > -0.5) return "text-red-600";
    if (value > -0.7) return "text-yellow-600";
    if (value < -1.0) return "text-orange-600";
    return "text-green-600";
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <nav className="text-sm text-gray-500 mb-2">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <a href="/monitoring" className="hover:underline">
              Dashboard
            </a>
            <span className="mx-2">/</span>
          </li>
          {currentModule.platform && (
            <>
              <li className="flex items-center">
                <span>{currentModule.platform}</span>
                <span className="mx-2">/</span>
              </li>
            </>
          )}
          {currentModule.leg && (
            <>
              <li className="flex items-center">
                <span>{currentModule.leg}</span>
                <span className="mx-2">/</span>
              </li>
            </>
          )}
          <li>
            <span className="font-medium text-gray-700">
              {currentModule.name}
            </span>
          </li>
        </ol>
      </nav>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
        <div>
          <h1 className="text-2xl font-bold">{currentModule.name}</h1>
          <div className="text-sm text-gray-600 mt-1">
            {currentModule.platform && `Platform: ${currentModule.platform}`}
            {currentModule.leg && ` | Leg: ${currentModule.leg}`}
            {currentModule.location && ` | Location: ${currentModule.location}`}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          Last Updated: {currentData.timestamp} {currentData.time}
        </div>
      </div>

      {/* Module Navigation Buttons (Top) */}
      {modules.length > 0 && (
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={handlePreviousModule}
            disabled={currentModuleIndex <= 0}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {currentModuleIndex > 0
              ? modules[currentModuleIndex - 1].name
              : "Previous"}
          </button>
          <span className="text-sm text-gray-500">
            Module {currentModuleIndex + 1} of {modules.length}
          </span>
          <button
            onClick={handleNextModule}
            disabled={currentModuleIndex >= modules.length - 1}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {currentModuleIndex < modules.length - 1
              ? modules[currentModuleIndex + 1].name
              : "Next"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left side - Camera Feed */}
        <div className="lg:flex-[2] bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">Live Camera Feed</h3>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-blue-500 hover:text-blue-700"
            >
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            </button>
          </div>
          <div
            className={
              isFullscreen
                ? "fixed inset-0 z-50 bg-black flex items-center justify-center"
                : ""
            }
          >
            {isFullscreen && (
              <button
                onClick={() => setIsFullscreen(false)}
                className="absolute top-4 right-4 text-white bg-gray-800 rounded-full p-2"
              >
                Close
              </button>
            )}
            <LiveCameraFeed />
          </div>
        </div>
        <div className="lg:flex-1 space-y-4">
          {["channel1", "channel2", "channel3", "channel4"].map(
            (channel, i) => (
              <div key={i} className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-lg font-semibold">VOLT {i + 1}</h3>
                <div
                  className={`text-3xl font-bold ${getStatusClass(
                    currentData[channel]
                  )} ${
                    [
                      "text-blue-600",
                      "text-green-600",
                      "text-purple-600",
                      "text-red-600",
                    ][i]
                  }`}
                >
                  {currentData[channel].toFixed(3)} V
                </div>
                <div className="text-sm text-gray-500">
                  {
                    [
                      "Reference Potential",
                      "Reference Potential",
                      "Reference Potential",
                      "Reference Potential",
                    ][i]
                  }
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Graph Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Real-time Potential Trends</h3>
          <button
            onClick={exportData}
            className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            <Download size={16} />
            Export Data
          </button>
        </div>
        <div className="w-full h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis domain={[-1, -0.7]} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="channel1"
                stroke="#2563eb"
                name="VOLT 1"
              />
              <Line
                type="monotone"
                dataKey="channel2"
                stroke="#16a34a"
                name="VOLT 2"
              />
              <Line
                type="monotone"
                dataKey="channel3"
                stroke="#9333ea"
                name="VOLT 3"
              />
              <Line
                type="monotone"
                dataKey="channel4"
                stroke="#dc2626"
                name="VOLT 4"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Reference Values Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Reference Values</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded p-4">
            <h4 className="font-medium mb-2">Healthy Range</h4>
            <p className="text-green-600 font-semibold">-0.85V to -0.95V</p>
            <p className="text-sm text-gray-600 mt-1">
              Optimal protection range for steel structures
            </p>
          </div>
          <div className="border border-gray-200 rounded p-4">
            <h4 className="font-medium mb-2">Warning Range</h4>
            <p className="text-yellow-600 font-semibold">-0.70V to -0.85V</p>
            <p className="text-sm text-gray-600 mt-1">
              Moderate protection, needs attention
            </p>
          </div>
          <div className="border border-gray-200 rounded p-4">
            <h4 className="font-medium mb-2">Overprotection Range</h4>
            <p className="text-orange-600 font-semibold">Below -1.0V</p>
            <p className="text-sm text-gray-600 mt-1">
              Risk of hydrogen embrittlement
            </p>
          </div>
          <div className="border border-gray-200 rounded p-4">
            <h4 className="font-medium mb-2">Critical Range</h4>
            <p className="text-red-600 font-semibold">Above -0.70V</p>
            <p className="text-sm text-gray-600 mt-1">
              Insufficient protection, immediate action required
            </p>
          </div>
        </div>
      </div>

      {/* Module Navigation Buttons (Bottom) */}
      {modules.length > 0 && (
        <div className="flex justify-between items-center mt-8 pb-8">
          <button
            onClick={handlePreviousModule}
            disabled={currentModuleIndex <= 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>
          <div className="text-sm text-gray-500">
            Module {currentModuleIndex + 1} of {modules.length}
          </div>
          <button
            onClick={handleNextModule}
            disabled={currentModuleIndex >= modules.length - 1}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Floating Navigation Arrows - For quick access during scrolling */}
      {modules.length > 0 && (
        <div className="fixed top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-4 pointer-events-none">
          <button
            onClick={handlePreviousModule}
            disabled={currentModuleIndex <= 0}
            className="p-2 rounded-full bg-white shadow-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto transition-opacity"
            aria-label="Previous module"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={handleNextModule}
            disabled={currentModuleIndex >= modules.length - 1}
            className="p-2 rounded-full bg-white shadow-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto transition-opacity"
            aria-label="Next module"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ModuleDetail;
